import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api, setToken, type AuthUser } from '@/lib/api'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null)
  const token = ref<string | null>(localStorage.getItem('auth_token'))
  const loading = ref(false)

  const isAuthenticated = computed(() => !!token.value && !!user.value)

  const initials = computed(() => {
    if (!user.value?.name) return '?'
    return user.value.name
      .split(' ')
      .map(w => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  })

  function setAuth(accessToken: string, u: AuthUser) {
    token.value = accessToken
    user.value = u
    setToken(accessToken)
  }

  function clearAuth() {
    token.value = null
    user.value = null
    setToken(null)
  }

  // Shared in-flight load so main.ts's init() and the router guard's ensureUser() don't both
  // hit /auth/me (and a failed load clears the token exactly once).
  let initPromise: Promise<void> | null = null

  function init(): Promise<void> {
    if (initPromise) return initPromise
    const saved = localStorage.getItem('auth_token')
    if (!saved) return Promise.resolve()
    token.value = saved
    setToken(saved)
    loading.value = true
    initPromise = (async () => {
      try {
        user.value = await api.auth.me()
      } catch {
        clearAuth()
      } finally {
        loading.value = false
      }
    })().finally(() => { initPromise = null })
    return initPromise
  }

  /** Resolves once the signed-in user is known. With a token but no user yet (fresh page load,
   * router's first navigation runs before main.ts's init() finishes) this awaits the load;
   * otherwise it is a no-op. Used by the router guard so role checks see the real role (I11). */
  async function ensureUser() {
    if (token.value && !user.value) await init()
  }

  async function devLogin(email?: string) {
    const data = await api.auth.devLogin(email)
    setAuth(data.accessToken, data.user)
    return data
  }

  async function logout() {
    try { await api.auth.logout() } catch { /* ignore */ }
    clearAuth()
  }

  return {
    user,
    token,
    loading,
    isAuthenticated,
    initials,
    init,
    ensureUser,
    devLogin,
    logout,
    setAuth,
    clearAuth,
  }
})
